import type { DroughtDetail } from "@shared/drought-detail";
import { LEVEL_LABELS } from "@/lib/level-badge";

export function droughtIntensityLabel(spei: number | undefined): "moderate" | "severe" | "extreme" | undefined {
  if (spei === undefined) return undefined;
  if (spei > -1.5) return "moderate";
  if (spei > -2.0) return "severe";
  return "extreme";
}

/** The one supporting sentence shown under the three drought scores
 * (methodology doc section 7.3). Undefined when there is no frequency to report. */
export function droughtSupportingText(detail: DroughtDetail, isFuture: boolean): string | undefined {
  const { frequencyPct, baselineFrequencyPct, intensitySpei, durationSeasons } = detail;
  if (frequencyPct === undefined) return undefined;

  const baseline =
    isFuture && baselineFrequencyPct !== undefined ? ` (baseline: ${Math.round(baselineFrequencyPct)}%)` : "";
  const intensity = droughtIntensityLabel(intensitySpei);
  const duration = durationSeasons === undefined ? "" : ` and lasts on average ${durationSeasons.toFixed(1)} season(s)`;
  const middle =
    intensity === undefined
      ? "No drought seasons in this period."
      : `When it occurs, it is typically ${intensity}${duration}.`;
  const labelScore = isFuture ? detail.combinedScore : detail.currentScore;
  const overall = labelScore === undefined ? "" : ` Overall drought risk: ${LEVEL_LABELS[labelScore]}.`;

  return `Drought affects ${Math.round(frequencyPct)}% of rainy seasons${baseline}. ${middle}${overall}`;
}

/** Signed one-decimal percent, e.g. 57.14 -> "+57.1%", -12.3 -> "-12.3%", 0 -> "0.0%".
 * One decimal so a value just under a score threshold (24.6) never reads as the
 * threshold itself ("+25%") next to a lower Change score. */
export function formatPctChange(pct: number | undefined): string | undefined {
  if (pct === undefined) return undefined;
  const fixed = pct.toFixed(1);
  // toFixed keeps the sign of tiny negatives ("-0.0"); show those as plain zero.
  if (Number(fixed) === 0) return "0.0%";
  return `${pct > 0 ? "+" : ""}${fixed}%`;
}
